<script setup lang="ts">
import { ref } from 'vue'

/**
 * Floating bottom-sheet shell: the inset rounded panel + drag handle + centered title header +
 * scrollable body slot, shared by the Canvas / Account / Search drawers (and matching the Collection
 * drawer language). The gesture/snap state lives in `useBottomSheet` in the consumer (snap heights
 * differ per sheet); this just renders the chrome and wires the pointer handlers.
 *
 * Pass the `sheet` (useBottomSheet return) and the computed `sheetStyle`. `role` / `aria-label` and
 * body padding stay with the consumer (fall-through attrs + the slot). Exposes `rootEl` (for
 * getBoundingClientRect) and `bodyEl` (for content measurement).
 */
interface SheetApi {
  isDragging: { value: boolean }
  onPointerDown: (event: PointerEvent) => void
  onPointerMove: (event: PointerEvent) => void
  onPointerUp: (event: PointerEvent) => void
}
interface Props {
  sheet: SheetApi
  title: string
  sheetStyle: Record<string, string>
  /** data-testid for the root; the handle gets `${testid}-handle`. */
  testid?: string
  /** Hide the body at peek (Canvas/Account); search keeps it always shown. */
  showBody?: boolean
  /** Floating (fixed, inset left-3/right-3 — the live default) vs static (for docs/embedding). */
  floating?: boolean
}
const props = withDefaults(defineProps<Props>(), { showBody: true, floating: true })

const rootEl = ref<HTMLElement | null>(null)
const bodyEl = ref<HTMLElement | null>(null)
defineExpose({ rootEl, bodyEl })
</script>

<template>
  <div
    ref="rootEl"
    class="pointer-events-auto flex flex-col overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-[0_-8px_30px_rgba(15,23,42,0.14)]"
    :class="props.floating ? 'fixed left-3 right-3' : ''"
    :style="props.sheetStyle"
    :data-testid="props.testid"
  >
    <!-- Drag handle -->
    <div
      class="relative z-10 flex shrink-0 touch-none justify-center bg-white py-1.5"
      :class="props.sheet.isDragging.value ? 'cursor-grabbing' : 'cursor-grab'"
      :data-testid="props.testid ? `${props.testid}-handle` : undefined"
      @pointerdown="props.sheet.onPointerDown"
      @pointermove="props.sheet.onPointerMove"
      @pointerup="props.sheet.onPointerUp"
      @pointercancel="props.sheet.onPointerUp"
    >
      <div class="h-1.5 w-12 rounded-full bg-gray-300"></div>
    </div>

    <!-- Header: centered title, vertically centered in the collapsed peek -->
    <div
      class="relative z-10 flex min-h-10 shrink-0 touch-none items-center justify-center bg-white px-2"
      @pointerdown="props.sheet.onPointerDown"
      @pointermove="props.sheet.onPointerMove"
      @pointerup="props.sheet.onPointerUp"
      @pointercancel="props.sheet.onPointerUp"
    >
      <h1 class="truncate text-lg font-semibold tracking-tight text-gray-900">{{ props.title }}</h1>
    </div>

    <!-- Body -->
    <div
      v-show="props.showBody"
      ref="bodyEl"
      class="min-h-0 flex-1 overflow-y-auto overscroll-contain"
    >
      <slot />
    </div>
  </div>
</template>
